import React, { useState } from "react";
import { Box, Text, Tip } from "grommet";
import { CircleInformation } from "grommet-icons";
import { formatCents } from "../../utils/money";
import { formatDateTimeAu } from "../../utils/date";

const formatValue = (v) => {
    if (v === null || v === undefined) return "--";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
};

// Most audit detail blobs are either {before, after} (an edit), {created:
// [...]} (an allocation batch save), or a flat dict of fields recorded at
// create/delete/restore time. Each gets its own readable rendering instead
// of a raw JSON dump.
const ChangeDetail = ({ detail }) => {
    if (detail.before && detail.after) {
        const keys = Array.from(
            new Set([
                ...Object.keys(detail.before),
                ...Object.keys(detail.after),
            ]),
        );
        const changed = keys.filter(
            (k) =>
                JSON.stringify(detail.before[k]) !==
                JSON.stringify(detail.after[k]),
        );
        if (changed.length === 0)
            return (
                <Text size="small" color="dark-5">
                    No fields changed
                </Text>
            );
        return (
            <Box gap="xsmall">
                {changed.map((k) => (
                    <Text key={k} size="small">
                        <strong>{k}:</strong> {formatValue(detail.before[k])} →{" "}
                        {formatValue(detail.after[k])}
                    </Text>
                ))}
            </Box>
        );
    }

    if (Array.isArray(detail.created)) {
        return (
            <Box gap="xsmall">
                {detail.created.map((item, i) => (
                    <Text key={item.id ?? i} size="small">
                        Funder #{item.funder_id}:{" "}
                        {item.portion_cents !== undefined
                            ? formatCents(item.portion_cents)
                            : "--"}
                        {item.predecessor_ids?.length > 0 &&
                            ` (replaces allocation${
                                item.predecessor_ids.length > 1 ? "s" : ""
                            } ${item.predecessor_ids
                                .map((id) => `#${id}`)
                                .join(", ")})`}
                    </Text>
                ))}
            </Box>
        );
    }

    return (
        <Box gap="xsmall">
            {Object.entries(detail).map(([k, v]) => (
                <Text key={k} size="small">
                    <strong>{k}:</strong>{" "}
                    {k.endsWith("_cents") ? formatCents(v) : formatValue(v)}
                </Text>
            ))}
        </Box>
    );
};

const summarize = (detail) => {
    if (!detail) return "";
    if (detail.before && detail.after) {
        const changed = Object.keys(detail.after).filter(
            (k) =>
                JSON.stringify(detail.before[k]) !==
                JSON.stringify(detail.after[k]),
        );
        return changed.length === 0
            ? "No fields changed"
            : `Changed: ${changed.join(", ")}`;
    }
    if (Array.isArray(detail.created)) {
        return `${detail.created.length} allocation(s)`;
    }
    return Object.keys(detail)
        .slice(0, 3)
        .map((k) => `${k}: ${formatValue(detail[k])}`)
        .join(", ");
};

const HEADER_COLUMNS = [
    { label: "Timestamp", width: "small" },
    { label: "User", width: "small" },
    { label: "Action", width: "small" },
    { label: "Resource Type", width: "small" },
    { label: "Resource ID", width: "small" },
];

const AuditLogTable = ({ loading, entries }) => {
    const [expandedId, setExpandedId] = useState(null);

    if (loading) return <Text margin={{ top: "medium" }}>Loading…</Text>;
    if (!entries || entries.length === 0) {
        return (
            <Text color="dark-5" margin={{ top: "medium" }}>
                No audit entries found
            </Text>
        );
    }

    return (
        <Box margin={{ top: "medium" }} gap="small">
            <Box
                direction="row"
                pad={{ horizontal: "small", bottom: "xsmall" }}
                gap="small"
                border={{ side: "bottom", color: "surface-border" }}
            >
                {HEADER_COLUMNS.map((col) => (
                    <Box key={col.label} width={col.width}>
                        <Text size="xsmall" weight="bold" color="dark-5">
                            {col.label}
                        </Text>
                    </Box>
                ))}
                <Box flex />
                <Box width="xsmall">
                    <Text size="xsmall" weight="bold" color="dark-5">
                        Details
                    </Text>
                </Box>
            </Box>
            {entries.map((entry) => {
                const expandable = Boolean(entry.detail);
                const expanded = expandedId === entry.id;
                return (
                    <Box
                        key={entry.id}
                        round="xsmall"
                        background="surface"
                        border={{ color: "surface-border" }}
                    >
                        <Box
                            direction="row"
                            align="center"
                            gap="small"
                            pad="small"
                            onClick={
                                expandable
                                    ? () =>
                                          setExpandedId(
                                              expanded ? null : entry.id,
                                          )
                                    : undefined
                            }
                            style={
                                expandable ? { cursor: "pointer" } : undefined
                            }
                        >
                            <Box width="small">
                                <Text size="small">
                                    {formatDateTimeAu(entry.timestamp)}
                                </Text>
                            </Box>
                            <Box width="small">
                                <Text size="small">
                                    {entry.username || "--"}
                                </Text>
                            </Box>
                            <Box width="small">
                                <Text size="small">{entry.action}</Text>
                            </Box>
                            <Box width="small">
                                <Text size="small">
                                    {entry.resourceType || "--"}
                                </Text>
                            </Box>
                            <Box width="small">
                                <Text size="small">
                                    {entry.resourceId || "--"}
                                </Text>
                            </Box>
                            <Box flex />
                            <Box width="xsmall" align="center">
                                {entry.detail && (
                                    <Tip
                                        content={
                                            <Box
                                                pad="small"
                                                background="surface"
                                                round="xsmall"
                                                border={{
                                                    color: "surface-border",
                                                }}
                                                width={{ max: "320px" }}
                                            >
                                                <Text size="small">
                                                    {summarize(entry.detail)}
                                                </Text>
                                            </Box>
                                        }
                                    >
                                        <CircleInformation
                                            size="small"
                                            color="dark-5"
                                        />
                                    </Tip>
                                )}
                            </Box>
                        </Box>
                        {expanded && (
                            <Box
                                pad={{
                                    horizontal: "small",
                                    top: "small",
                                    bottom: "small",
                                }}
                                border={{
                                    side: "top",
                                    color: "surface-border",
                                }}
                            >
                                <ChangeDetail detail={entry.detail} />
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export { AuditLogTable };
